import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message, Tabs } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getMaintenanceRequestById, deleteMaintenanceRequest } from '@/api/maintenance';
import { MaintenanceRequest } from '@/types/maintenance';
import { useAuth } from '@/contexts/AuthContext';
import MaintenanceModal from '@/components/features/maintenance/MaintenanceModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const maintenanceStatusColors: Record<MaintenanceRequest['status'], string> = {
  'Open': 'blue',
  'In Progress': 'processing',
  'On Hold': 'warning',
  'Resolved': 'success'
};

const maintenancePriorityColors: Record<MaintenanceRequest['priority'], string> = {
  'Low': 'green',
  'Medium': 'orange',
  'High': 'red',
  'Urgent': 'red'
};

const MaintenanceDetailsPage: React.FC = () => {
  const { t } = useTranslation(['maintenance', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { maintenanceRequest: MaintenanceRequest } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - CRITICAL: Only fetch if no state data is available
  const { data: maintenanceRequest, isLoading } = useQuery({
    queryKey: ['maintenance', id],
    queryFn: () => getMaintenanceRequestById(id!, firestoreUser!),
    enabled: !!id && !state?.maintenanceRequest && !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayMaintenanceRequest = state?.maintenanceRequest || maintenanceRequest;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (maintenanceRequestId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteMaintenanceRequest(maintenanceRequestId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('maintenance:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/maintenance');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['maintenance', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Loading state - only show if no state data is available
  if (isLoading && !state?.maintenanceRequest) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayMaintenanceRequest) {
    return <Card>Maintenance request not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayMaintenanceRequest.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Tabs */}
      <Card>
        <Tabs
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: t('maintenance:detailsLabel'),
              children: (
                <Descriptions bordered column={1}>
                  <Descriptions.Item label={t('maintenance:table.title')}>
                    <Text strong>{displayMaintenanceRequest.title}</Text>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.description')}>
                    <Paragraph>{displayMaintenanceRequest.description}</Paragraph>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.status')}>
                    <Tag color={maintenanceStatusColors[displayMaintenanceRequest.status]}>
                      {displayMaintenanceRequest.status}
                    </Tag>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.priority')}>
                    <Tag color={maintenancePriorityColors[displayMaintenanceRequest.priority]}>
                      {displayMaintenanceRequest.priority}
                    </Tag>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.reportedBy')}>
                    {displayMaintenanceRequest.reportedBy.name}
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.block')}>
                    {displayMaintenanceRequest.block.name}
                  </Descriptions.Item>
                  
                  {displayMaintenanceRequest.apartmentUnit && (
                    <Descriptions.Item label={t('maintenance:table.apartmentUnit')}>
                      {displayMaintenanceRequest.apartmentUnit.name}
                    </Descriptions.Item>
                  )}
                  
                  {displayMaintenanceRequest.assignedTo && (
                    <Descriptions.Item label={t('maintenance:table.assignedTo')}>
                      {displayMaintenanceRequest.assignedTo.name}
                    </Descriptions.Item>
                  )}
                  
                  {displayMaintenanceRequest.resolutionNotes && (
                    <Descriptions.Item label={t('maintenance:table.resolutionNotes')}>
                      <Paragraph>{displayMaintenanceRequest.resolutionNotes}</Paragraph>
                    </Descriptions.Item>
                  )}
                  
                  <Descriptions.Item label={t('maintenance:table.createdAt')}>
                    {dayjs(displayMaintenanceRequest.createdAt).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('maintenance:table.updatedAt')}>
                    {dayjs(displayMaintenanceRequest.updatedAt).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                  
                  {displayMaintenanceRequest.attachments && displayMaintenanceRequest.attachments.length > 0 && (
                    <Descriptions.Item label={t('maintenance:table.attachments')}>
                      {displayMaintenanceRequest.attachments.length} {t('common:items')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
            {
              key: 'activity',
              label: t('maintenance:activity'),
              children: (
                <div>
                  {displayMaintenanceRequest.activityRecords && displayMaintenanceRequest.activityRecords.length > 0 ? (
                    <div>
                      {displayMaintenanceRequest.activityRecords.map((record, index) => (
                        <Card key={index} size="small" style={{ marginBottom: 8 }}>
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Text strong>{record.type}</Text>
                              <br />
                              <Text type="secondary">{record.performedBy.displayName}</Text>
                            </Col>
                            <Col>
                              <Text type="secondary">
                                {dayjs(record.timestamp).format('YYYY-MM-DD HH:mm')}
                              </Text>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">{t('maintenance:noActivity')}</Text>
                  )}
                </div>
              ),
            },
            {
              key: 'comments',
              label: t('maintenance:comments'),
              children: (
                <div>
                  {displayMaintenanceRequest.comments && displayMaintenanceRequest.comments.length > 0 ? (
                    <div>
                      {displayMaintenanceRequest.comments.map((comment, index) => (
                        <Card key={index} size="small" style={{ marginBottom: 8 }}>
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Text strong>{comment.author.name}</Text>
                              <br />
                              <Text>{comment.content}</Text>
                            </Col>
                            <Col>
                              <Text type="secondary">
                                {dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Text>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">{t('maintenance:noComments')}</Text>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Edit Modal */}
      <MaintenanceModal
        open={isEditModalOpen}
        maintenanceRequest={displayMaintenanceRequest}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('maintenance:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default MaintenanceDetailsPage; 